import { useCallback, useEffect, useRef, useState, type ComponentProps } from 'react'
import { cn } from '@app/lib/cn'

/**
 * Data-dense table primitives: 13px, hairline rules, sticky header.
 *
 * `TableScroller` owns the scroll context — the sticky header only works inside
 * it, so always wrap <Table> in one.
 */

/**
 * A thin, ALWAYS-visible scrollbar on the scroll container.
 *
 * macOS hides overlay scrollbars until you scroll, so a table that ran wider
 * than its card looked complete — the missing columns were invisible until
 * somebody happened to swipe sideways. Styling `::-webkit-scrollbar` opts the
 * element out of overlay mode in WebKit/Blink; `scrollbar-width`/`-color` does
 * the same in Gecko and in Safari 18+.
 */
const SCROLLBAR = [
  '[scrollbar-width:thin]',
  '[scrollbar-color:var(--color-hairline)_transparent]',
  '[&::-webkit-scrollbar]:h-1.5',
  '[&::-webkit-scrollbar]:w-1.5',
  '[&::-webkit-scrollbar-track]:bg-transparent',
  '[&::-webkit-scrollbar-thumb]:rounded-xs',
  '[&::-webkit-scrollbar-thumb]:bg-hairline',
  '[&::-webkit-scrollbar-thumb:hover]:bg-ink-muted/45',
].join(' ')

/** Ignore sub-pixel rounding when deciding whether an edge is scrollable. */
const EDGE_EPSILON = 1

export function TableScroller({ className, children, ...props }: ComponentProps<'div'>) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: false, end: false })

  const measure = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    const offset = Math.abs(el.scrollLeft)
    setEdges((prev) => {
      const start = offset > EDGE_EPSILON
      const end = max > EDGE_EPSILON && offset < max - EDGE_EPSILON
      // Same state → same object, so scrolling doesn't re-render the table.
      return prev.start === start && prev.end === end ? prev : { start, end }
    })
  }, [])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    measure()
    /*
     * Rows arrive after the first paint and columns come and go with the
     * viewport, so the affordance is re-measured on every size change of both
     * the viewport-side box and the table inside it — a scroll listener alone
     * would keep showing yesterday's answer.
     */
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    for (const child of Array.from(el.children)) observer.observe(child)
    return () => observer.disconnect()
  }, [measure])

  return (
    <div className="relative min-w-0">
      <div
        {...props}
        ref={scrollerRef}
        onScroll={measure}
        className={cn('relative w-full overflow-auto', SCROLLBAR, className)}
      >
        {children}
      </div>
      <ScrollEdge side="start" visible={edges.start} />
      <ScrollEdge side="end" visible={edges.end} />
    </div>
  )
}

/**
 * The soft ink shading over a scrollable edge: "there is more table this way".
 * Purely decorative and never a hit target — the scroll container underneath
 * has to stay draggable.
 */
function ScrollEdge({ side, visible }: { side: 'start' | 'end'; visible: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-y-0 w-6 transition-opacity',
        side === 'start'
          ? 'left-0 bg-[linear-gradient(to_right,color-mix(in_oklab,var(--color-kosele)_10%,transparent),transparent)]'
          : 'right-0 bg-[linear-gradient(to_left,color-mix(in_oklab,var(--color-kosele)_10%,transparent),transparent)]',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    />
  )
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <table
      className={cn('w-full caption-bottom border-collapse text-data tnum', className)}
      {...props}
    />
  )
}

export function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
  return (
    <thead
      className={cn(
        'sticky top-0 z-10 bg-kagit [&_tr]:border-b [&_tr]:border-hairline',
        // Sticky cells lose their border in Safari; draw it with a shadow instead.
        'after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-hairline',
        className,
      )}
      {...props}
    />
  )
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'border-b border-hairline transition-colors hover:bg-kordovan/4 data-[state=selected]:bg-kordovan/8',
        className,
      )}
      {...props}
    />
  )
}

export function TableHead({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      scope="col"
      className={cn(
        'h-8 px-2.5 text-left align-middle font-display text-micro font-semibold uppercase tracking-[0.06em] text-ink-muted',
        'first:pl-3.5 last:pr-3.5',
        className,
      )}
      {...props}
    />
  )
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      className={cn('h-8 px-2.5 align-middle first:pl-3.5 last:pr-3.5', className)}
      {...props}
    />
  )
}

/** Right-aligned mono cell for quantities and amounts. */
export function TableNumCell({ className, ...props }: ComponentProps<'td'>) {
  return <TableCell className={cn('text-right font-mono', className)} {...props} />
}

export function TableCaption({ className, ...props }: ComponentProps<'caption'>) {
  return <caption className={cn('py-2 text-micro text-ink-muted', className)} {...props} />
}
